import { useMemo } from 'react';
import { IMilestoneDropdownOption, MILESTONE_FILTER_VALUES } from '../types';
import { DivisionMileStone } from '../../../api/divisionMileStones/types';

export interface MilestoneCountData {
       exceptionCount: number;
       alertsCount: number;
       notesCount: number;
       generalCommentsCount: number;
}

export interface MilestoneSpecificCounts {
       [milestoneName: string]: MilestoneCountData;
}

export interface MilestoneDataHookProps {
       milestones?: DivisionMileStone[];
       milestoneSpecificCounts?: MilestoneSpecificCounts;
}

export interface MilestoneDataResult {
       totalCounts: MilestoneCountData;
       milestoneOptions: IMilestoneDropdownOption[];
       defaultOption: IMilestoneDropdownOption;
       arrayOptions: IMilestoneDropdownOption[];
}

export const useMilestoneData = ({ milestones, milestoneSpecificCounts }: MilestoneDataHookProps): MilestoneDataResult => {

       // Calculate total counts from milestone-specific counts
       const totalCounts = useMemo(() => {
              if (!milestoneSpecificCounts) {
                     return { alertsCount: 0, exceptionCount: 0, notesCount: 0, generalCommentsCount: 0 };
              }

              const total = {
                     exceptionCount: 0,
                     alertsCount: 0,
                     notesCount: 0,
                     generalCommentsCount: 0
              };

              // Sum up all milestone-specific counts
              Object.values(milestoneSpecificCounts).forEach(counts => {
                     total.exceptionCount += counts.exceptionCount;
                     total.alertsCount += counts.alertsCount;
                     total.notesCount += counts.notesCount;
                     total.generalCommentsCount += counts.generalCommentsCount;
              });

              return total;
       }, [milestoneSpecificCounts]);

       // Create milestone options
       const milestoneOptions = useMemo(() => {
              if (!milestones) return [];

              // Create a map to ensure unique milestone names
              const uniqueMilestoneMap = new Map<string, DivisionMileStone>();
              milestones.forEach((milestone: DivisionMileStone) => {
                     if (!uniqueMilestoneMap.has(milestone.mileStoneName)) {
                            uniqueMilestoneMap.set(milestone.mileStoneName, milestone);
                     }
              });

              return Array.from(uniqueMilestoneMap.values()).map((milestone: DivisionMileStone) => {
                     const counts = milestoneSpecificCounts?.[milestone.mileStoneName] || {
                            exceptionCount: 0,
                            alertsCount: 0,
                            notesCount: 0,
                            generalCommentsCount: 0
                     };

                     return {
                            label: milestone.mileStoneName,
                            value: milestone.mileStoneName,
                            exceptionCount: counts.exceptionCount,
                            alertsCount: counts.alertsCount,
                            notesCount: counts.notesCount,
                            generalCommentsCount: counts.generalCommentsCount,
                            milestone: milestone
                     };
              });
       }, [milestones, milestoneSpecificCounts]);

       // Create General Comments option separately if it exists
       const generalCommentsOption = useMemo(() => {
              const generalCommentsCounts = milestoneSpecificCounts?.["General Comments"];
              const hasGeneralComments = generalCommentsCounts && generalCommentsCounts.generalCommentsCount > 0;

              if (hasGeneralComments) {
                     return {
                            label: "General Comments",
                            value: "General Comments",
                            exceptionCount: generalCommentsCounts.exceptionCount,
                            alertsCount: generalCommentsCounts.alertsCount,
                            notesCount: generalCommentsCounts.notesCount,
                            generalCommentsCount: generalCommentsCounts.generalCommentsCount,
                            milestone: null // No actual milestone object for general comments
                     };
              }
              return null;
       }, [milestoneSpecificCounts]);

       // Create default option
       const defaultOption = useMemo(() => ({
              label: 'All Milestones',
              value: MILESTONE_FILTER_VALUES.allMilestones,
              exceptionCount: totalCounts.exceptionCount,
              alertsCount: totalCounts.alertsCount,
              notesCount: totalCounts.notesCount,
              generalCommentsCount: totalCounts.generalCommentsCount,
       }), [totalCounts]);

       // Combine all options with General Comments in second position
       const arrayOptions = useMemo(() => {
              const options = [defaultOption];

              // Add General Comments as second option if it exists
              if (generalCommentsOption) {
                     options.push(generalCommentsOption);
              }

              // Add regular milestone options
              options.push(...milestoneOptions);

              return options;
       }, [defaultOption, generalCommentsOption, milestoneOptions]);

       return {
              totalCounts,
              milestoneOptions,
              defaultOption,
              arrayOptions
       };
};

export default useMilestoneData; 